import { Expedition } from '../types';
import { Language } from '../translations';

export interface SEOAlternate {
  lang: string;
  href: string;
}

export interface SEOData {
  title: string;
  description: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  schemaData?: object | null;
  currentLang?: Language;
  alternates?: SEOAlternate[];
}

const DEFAULT_IMAGE = 'https://iuixlsqrlfkfnvnckgij.supabase.co/storage/v1/object/public/expeditions/Laguna%20Cerro%20Castillo/trekking-laguna-cerro-castillo-turquesa.jpg';
const DEFAULT_URL = 'https://fisuraexpeditions.com';
const SITE_NAME = 'Fisura Expeditions';

/**
 * Helper to update meta tags dynamically
 */
function setMetaTag(attributeName: 'name' | 'property', attributeValue: string, content: string) {
  if (typeof document === 'undefined') return;
  let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attributeName, attributeValue);
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
}

/**
 * Helper to update canonical URL
 */
function setCanonicalUrl(url: string) {
  if (typeof document === 'undefined') return;
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

/**
 * Helper to update or inject hreflang alternate links for Google international SEO
 */
function setHreflangTags(alternates: SEOAlternate[]) {
  if (typeof document === 'undefined') return;
  // Remove existing alternate hreflang tags to prevent duplicate accummulation
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

  alternates.forEach(({ lang, href }) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', href);
    document.head.appendChild(link);
  });
}

/**
 * Helper to update Schema.org JSON-LD script
 */
function setJsonLd(schemaData: object | null) {
  if (typeof document === 'undefined') return;
  const scriptId = 'schema-jsonld';
  let script = document.getElementById(scriptId) as HTMLScriptElement | null;

  if (!schemaData) {
    if (script) script.remove();
    return;
  }

  if (!script) {
    script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(schemaData, null, 2);
}

/**
 * Primary function to update all SEO tags on page route change
 */
export function updateSEO(data: SEOData) {
  if (typeof document === 'undefined') return;

  const {
    title,
    description,
    keywords = [],
    image = DEFAULT_IMAGE,
    url = typeof window !== 'undefined' ? window.location.href : DEFAULT_URL,
    type = 'website',
    schemaData = null,
    currentLang = 'es',
    alternates = []
  } = data;

  // Title & Document Language Attribute
  document.title = title;
  document.documentElement.lang = currentLang;

  // Standard Meta Tags
  setMetaTag('name', 'description', description);
  if (keywords.length > 0) {
    setMetaTag('name', 'keywords', keywords.join(', '));
  }

  // Open Graph
  const ogLocale = currentLang === 'en' ? 'en_US' : currentLang === 'pt' ? 'pt_BR' : currentLang === 'fr' ? 'fr_FR' : 'es_CL';
  setMetaTag('property', 'og:site_name', SITE_NAME);
  setMetaTag('property', 'og:locale', ogLocale);
  setMetaTag('property', 'og:title', title);
  setMetaTag('property', 'og:description', description);
  setMetaTag('property', 'og:image', image);
  setMetaTag('property', 'og:url', url);
  setMetaTag('property', 'og:type', type);

  // Twitter
  setMetaTag('name', 'twitter:card', 'summary_large_image');
  setMetaTag('name', 'twitter:title', title);
  setMetaTag('name', 'twitter:description', description);
  setMetaTag('name', 'twitter:image', image);
  setMetaTag('name', 'twitter:url', url);

  // Canonical URL
  setCanonicalUrl(url);

  // Hreflang alternates
  if (alternates.length > 0) {
    setHreflangTags(alternates);
  }

  // JSON-LD Schema
  setJsonLd(schemaData);
}

/**
 * Builds SEO data specifically for an Expedition
 */
export function getExpeditionSEO(exp: Expedition, lang: Language, currentUrl?: string): SEOData {
  const isEn = lang === 'en';
  
  // Custom SEO fields or fallback to main fields
  const titleText = isEn 
    ? (exp.seo_title_en || exp.name_en || exp.name_es)
    : (exp.seo_title_es || exp.name_es);
    
  const title = `${titleText} | Fisura Expeditions`;

  const rawDesc = isEn
    ? (exp.seo_desc_en || exp.desc_en || exp.desc_es)
    : (exp.seo_desc_es || exp.desc_es);

  // Clean description text (max ~160 chars for search snippets)
  const description = rawDesc
    ? rawDesc.replace(/[\r\n]+/g, ' ').substring(0, 160).trim()
    : 'Expedición de montaña en Villa Cerro Castillo, Aysén.';

  const defaultKeywords = [
    'Fisura Expeditions',
    'Cerro Castillo',
    'Aysén',
    'Guías de Montaña',
    exp.category
  ];
  
  const customKeywords = isEn ? exp.keywords_en : exp.keywords_es;
  const keywords = Array.from(new Set([...(customKeywords || []), ...defaultKeywords]));

  // Ensure absolute image URL
  let imageUrl = exp.image || DEFAULT_IMAGE;
  if (imageUrl.startsWith('/')) {
    imageUrl = `https://fisuraexpeditions.com${imageUrl}`;
  }

  const expUrl = currentUrl || (isEn ? `https://fisuraexpeditions.com/en/expeditions/${exp.slug}` : `https://fisuraexpeditions.com/expeditions/${exp.slug}`);

  // Build TouristTrip / Product Schema.org JSON-LD
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    'name': titleText,
    'description': description,
    'image': [imageUrl],
    'touristType': [exp.category, 'Mountaineering', 'Trekking', 'Outdoor Adventure'],
    'offers': exp.price_from ? {
      '@type': 'Offer',
      'price': exp.price_from,
      'priceCurrency': 'CLP',
      'availability': 'https://schema.org/InStock',
      'url': expUrl
    } : undefined,
    'provider': {
      '@type': 'OutdoorStore',
      'name': 'Fisura Expeditions',
      'url': 'https://fisuraexpeditions.com',
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': 'Villa Cerro Castillo',
        'addressRegion': 'Aysén',
        'addressCountry': 'Chile'
      }
    },
    'itinerary': exp.itinerary_es?.map(item => ({
      '@type': 'ItemList',
      'name': `Día ${item.day}${item.title ? `: ${item.title}` : ''}`,
      'description': item.desc
    }))
  };

  return {
    title,
    description,
    keywords,
    image: imageUrl,
    url: expUrl,
    type: 'product',
    schemaData
  };
}

/**
 * Builds SEO data specifically for the Homepage
 */
export function getHomeSEO(lang: Language, currentUrl: string, t?: Record<string, string>): SEOData {
  const isEn = lang === 'en';
  const title = t?.home_seo_title || (isEn 
    ? 'Fisura Expeditions | Trekking & Climbing Tours in Patagonia'
    : 'Fisura Expeditions | Tours de trekking y escalada en Patagonia');

  const description = t?.home_seo_desc || (isEn
    ? 'Official tour operator in Villa Cerro Castillo and Aysén. Specialists in trekking tours to Laguna Cerro Castillo, Las Horquetas traverse, and rock climbing in Patagonia.'
    : 'Operador turístico oficial en Villa Cerro Castillo y Aysén. Especialistas en tours de trekking a Laguna Cerro Castillo, Travesía Las Horquetas y escalada técnica en Patagonia.');

  const keywords = [
    'Fisura Expeditions',
    'Tours de trekking Patagonia',
    'Trekking Cerro Castillo',
    'Escalada en Patagonia',
    'Travesía Las Horquetas',
    'Guías de Montaña Aysén',
    'Patagonia Tour Operator',
    'Villa Cerro Castillo'
  ];

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    'name': 'Fisura Expeditions',
    'alternateName': 'Fisura Expeditions Patagonia',
    'description': description,
    'url': currentUrl,
    'image': DEFAULT_IMAGE,
    'telephone': '+56984168019',
    'priceRange': '$$',
    'areaServed': [
      {
        '@type': 'AdministrativeArea',
        'name': 'Patagonia'
      },
      {
        '@type': 'AdministrativeArea',
        'name': 'Región de Aysén'
      },
      {
        '@type': 'Place',
        'name': 'Cerro Castillo'
      }
    ],
    'address': {
      '@type': 'PostalAddress',
      'addressLocality': 'Villa Cerro Castillo',
      'addressRegion': 'Aysén',
      'addressCountry': 'CL'
    },
    'sameAs': [
      'https://www.instagram.com/fisuraexpeditions/'
    ]
  };

  return {
    title,
    description,
    keywords,
    image: DEFAULT_IMAGE,
    url: currentUrl,
    type: 'website',
    schemaData
  };
}

